import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
import { CSSTransition } from "react-transition-group";

/** Today in the visitor's own timezone as YYYY-MM-DD — `toISOString` would give UTC and can be a day off. */
function today() {
  const now = new Date();
  return [
    now.getFullYear(),
    `${now.getMonth() + 1}`.padStart(2, "0"),
    `${now.getDate()}`.padStart(2, "0"),
  ].join("-");
}

export default function GiftCardRecipientTriggerContent({
  open,
  recipientData,
  setRecipientData,
  recipientErrors,
  idPrefix,
}) {
  const [height, setHeight] = useState(0);
  const nodeRef = useRef();
  const roRef = useRef(null);

  useEffect(() => {
    return () => roRef.current?.disconnect();
  }, []);

  return (
    <CSSTransition
      appear={true}
      mountOnEnter={true}
      unmountOnExit={true}
      in={open}
      nodeRef={nodeRef}
      classNames="gift-card-recipient-form__content"
      onEntering={() => {
        if (!nodeRef.current) return;
        const inner = nodeRef.current.firstElementChild;
        if (!inner) return;
        roRef.current = new ResizeObserver(() => {
          if (nodeRef.current) setHeight(nodeRef.current.scrollHeight);
        });
        roRef.current.observe(inner);
      }}
      onEntered={() => {
        roRef.current?.disconnect();
        roRef.current = null;
      }}
      onExited={() => {
        roRef.current?.disconnect();
        roRef.current = null;
      }}
      addEndListener={(done) => {
        nodeRef.current?.addEventListener("transitionend", done, false);
      }}
    >
      <div
        className="gift-card-recipient-form__content"
        ref={nodeRef}
        style={{ "--height": `${height}px` }}
      >
        <div className="gift-card-recipient-form__content-inner">
          <label className="gift-card-recipient-form__input-wrapper input-label">
            <input
              placeholder={
                window.localeStrings.giftCardRecipientForm.email_label
              }
              type="text"
              inputmode="email"
              id={`${idPrefix}gift-card-email`}
              aria-invalid="false"
              value={recipientData.email || ""}
              onInput={(e) =>
                setRecipientData({
                  ...recipientData,
                  email: e.currentTarget.value,
                })
              }
              aria-describedby={
                recipientErrors.email
                  ? `${idPrefix}gift-card-email-error`
                  : undefined
              }
            />
            <div
              className={`input-label__text visually-hidden ${recipientErrors.email ? "error" : ""}`}
            >
              {window.localeStrings.giftCardRecipientForm.email_label}
            </div>

            <span
              className="input-label__error"
              id={`${idPrefix}gift-card-email-error`}
            >
              {recipientErrors.emailIsInvalid
                ? window.localeStrings.giftCardRecipientForm.email_invalid
                : window.localeStrings.giftCardRecipientForm.email_error}
            </span>
          </label>

          <label className="gift-card-recipient-form__input-wrapper input-label">
            <input
              autocomplete="name"
              type="text"
              id={`${idPrefix}gift-card-name`}
              placeholder={
                window.localeStrings.giftCardRecipientForm.name_label
              }
              aria-invalid="false"
              value={recipientData.name || ""}
              onInput={(e) =>
                setRecipientData({
                  ...recipientData,
                  name: e.currentTarget.value,
                })
              }
            />
            <div className="input-label__text visually-hidden">
              {window.localeStrings.giftCardRecipientForm.name_label}
            </div>
          </label>

          <label className="gift-card-recipient-form__input-wrapper input-label">
            <textarea
              className="gift-card-recipient-form__message"
              placeholder={
                window.localeStrings.giftCardRecipientForm.message_label
              }
              maxlength="200"
              aria-invalid="false"
              id={`${idPrefix}gift-card-message`}
              value={recipientData.message || ""}
              onInput={(e) =>
                setRecipientData({
                  ...recipientData,
                  message: e.currentTarget.value,
                })
              }
            ></textarea>
            <div className="input-label__text visually-hidden">
              {window.localeStrings.giftCardRecipientForm.message_label}
            </div>
          </label>

          <label className="gift-card-recipient-form__input-wrapper input-label">
            <input
              className="send_on"
              autocomplete="send_on"
              type="date"
              id={`${idPrefix}gift-card-send-on`}
              placeholder={window.localeStrings.giftCardRecipientForm.send_on}
              pattern="\d{4}-\d{2}-\d{2}"
              min={today()}
              value={recipientData.send_on || ""}
              aria-invalid="false"
              onInput={(e) =>
                setRecipientData({
                  ...recipientData,
                  send_on: e.currentTarget.value,
                })
              }
              aria-describedby={
                recipientErrors.email
                  ? `${idPrefix}gift-card-send-on-error`
                  : undefined
              }
            />
            <div
              className={`input-label__text ${recipientErrors.send_on ? "error" : ""}`}
            >
              {window.localeStrings.giftCardRecipientForm.send_on_label}
            </div>
            <span
              className="input-label__error"
              id={`${idPrefix}gift-card-send-on-error`}
            >
              {window.localeStrings.giftCardRecipientForm.send_on_error}
            </span>
          </label>
        </div>
      </div>
    </CSSTransition>
  );
}
